import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { status } from 'constants/general';

import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import categoriesStrings from 'modules/categories/strings';

import initialIcon from 'assets/images/empty-providers.svg';

import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';
import themeData from 'utils/theme/themeData';
import AddPricelistProductsModal from 'modules/modals/AddPricelistProductsModal';
import { getServicesTypes } from 'constants/categories';
import { selectedHealthPlanCountrySelector } from 'modules/healthPlans/healthPlansSlice';
import {
  Container,
  Center,
  Img,
  Text,
  Button,
  Wrapper,
  extendTableStyle,
} from './PharmaciesProductsComponent.style';

const isProductConfiguration = item => Boolean(item?.productKey);

const PharmaciesProductsComponent = ({
  isCreateProductModalHidden,
  setIsCreateProductModalHidden,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleRemoveProduct,
  priceListId,
}) => {
  const [productsData, setProductsData] = useState([]);
  const [productsRecords, setProductsRecords] = useState([]);
  const selectedHealthPlanCountry = useSelector(selectedHealthPlanCountrySelector);
  const services = useSelector(state => state.categories.services);

  const getMainRowItems = data => {
    const rows = [];
    data?.forEach(item => {
      const { id, productName, priceValue, discountTypeId } = item;
      const servicesTypes = getServicesTypes(selectedHealthPlanCountry?.currency?.name);
      const currentType = servicesTypes[discountTypeId]?.label || '';
      rows.push({
        id,
        rowData: item,
        rowContent: [
          {
            itemContent: productName,
            itemType: TableItemType.Text,
            isItemClickable: false,
          },
          {
            itemContent: `${priceValue} ${currentType}`,
            itemType: TableItemType.Text,
            isItemClickable: false,
          },
        ],
        editButtons: [
          {
            btnId: 1,
            btnIcon: '"\\e91d"',
            btnLabel: 'Remove',
            btnFn: () => handleRemoveProduct(item),
            btnTextColor: themeData.colors.redMain,
          },
        ],
      });
    });

    return rows;
  };

  useEffect(() => {
    setProductsData((services.data || []).filter(isProductConfiguration));
  }, [services.data]);

  useEffect(() => {
    setProductsRecords(() => getMainRowItems(productsData));
  }, [productsData]);

  const isEmpty = services.status === status.SUCCESS && productsData.length === 0 && !searchQuery;

  return (
    <Container>
      {isEmpty ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{categoriesStrings.pharmacyProducts.emptyState}</Text>
          <Button onClick={() => setIsCreateProductModalHidden(false)}>
            {categoriesStrings.pharmacyProducts.addProducts}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={() => setIsCreateProductModalHidden(false)}
          >
            {categoriesStrings.pharmacyProducts.addProducts}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="pharmacyproductssearch"
          />
          <Table
            latestDataLength={services.latestDataLength}
            dataStatus={services.status}
            columns={categoriesStrings.pharmacyProductsTable.gridHeaderList}
            rows={productsRecords}
            searchQuery={searchQuery}
            handleShowMoreClick={handleShowMoreClick}
            extendTableStyle={extendTableStyle}
          />
        </Wrapper>
      )}

      {!isCreateProductModalHidden && (
        <AddPricelistProductsModal
          onClose={() => setIsCreateProductModalHidden(true)}
          priceListId={priceListId}
          modalStrings={categoriesStrings.addProductsModal}
        />
      )}
    </Container>
  );
};

PharmaciesProductsComponent.propTypes = {
  isCreateProductModalHidden: PropTypes.bool.isRequired,
  setIsCreateProductModalHidden: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  handleShowMoreClick: PropTypes.func.isRequired,
  handleRemoveProduct: PropTypes.func.isRequired,
  priceListId: PropTypes.string.isRequired,
};

export default PharmaciesProductsComponent;
